import { Trans } from '../../atoms'
import StepItemOrdine from '../../molecules/StepItemOrdine/StepItemOrdine'
import ProdottoPersonalizzazioneCard from '../../molecules/ProdottoPersonalizzazioneCard/ProdottoPersonalizzazioneCard'
import { getRigheOrdineFromPersonalizzazione } from '../../../utils/postOrdine'
import { UploadBozzaDigitale } from './UploadBozzaDigitale'
import { ModificaDimensione } from './ModificaDimensione'
import { ModificaColori } from './ModificaColori'
import { priceNotation } from '../../../utils/safe'
import useTrans from '../../../hooks/useTrans'
import { parseFontSize } from '@utils/editor-bozze'

const PersonalizzazioneOrdineDetail = ({
  personalizzazione,
  ordine,
  mostraLoghi,
  mostraNote,
  mostraBozza,
  mostraPrezziFornitore,
  personalizzazioneFornitore,
  uploadBozzaDigitale,
  handleUploadBozzaDigitale,
  modificaDimensione,
  modificaColori,
  isFornitore,
  fromAdmin,
}) => {
  const t = useTrans()
  const righe_per_personalizzazione = getRigheOrdineFromPersonalizzazione(personalizzazione)

  const getAreaStampa = (personalizzazione, articolo) => {
    const areeStampa = personalizzazione?.aree_stampa || []
    if (!articolo?.colore?.id) return areeStampa[0]
    return areeStampa.find((area) => area.colore?.id === articolo.colore.id)
  }

  const renderDimensione = (personalizzazione_posizione) => {
    return (
      <>
        {personalizzazione_posizione.altezza || personalizzazione_posizione.larghezza ? (
          <>
            {renderField('Larghezza', <>{personalizzazione_posizione.larghezza} CM</>)}
            {renderField('Altezza', <>{personalizzazione_posizione.altezza} CM</>)}
          </>
        ) : (
          renderField('Dimensione', personalizzazione_posizione.dimensione.nome)
        )}
      </>
    )
  }

  const formatEditorValue = (value) => {
    if (value === null || value === undefined || value === '') return null

    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return `${value}`

    if (Array.isArray(value)) {
      const values = value.map(formatEditorValue).filter(Boolean)
      return values.length ? values.join(', ') : null
    }

    if (typeof value === 'object') {
      if (value.nome) return value.nome
      if (value.name) return value.name
      if (value.label) return value.label
      if (value.pantone) return value.pantone
      if (value.value && typeof value.value !== 'object') return `${value.value}`

      const primitiveEntries = Object.entries(value)
        .filter(([, itemValue]) => ['string', 'number', 'boolean'].includes(typeof itemValue))
        .map(([, itemValue]) => `${itemValue}`)

      return primitiveEntries.length ? primitiveEntries.join(', ') : null
    }

    return null
  }

  const getEditorFileHref = (file) => file?.url || file?.href || file?.file_originale?.url

  const getFileBaseName = (value) => {
    if (!value || typeof value !== 'string') return null

    const normalized = value.split('?')[0].split('#')[0]
    const parts = normalized.split(/[\\/]/)
    return parts[parts.length - 1] || null
  }

  const getEditorFileName = (file, fallback) =>
    getFileBaseName(file?.name) ||
    getFileBaseName(file?.filename) ||
    getFileBaseName(file?.file_originale?.name) ||
    fallback

  const renderField = (label, value, uppercaseValue = true) => (
    <p>
      <span className="personalizzazione-ordine-detail__conf-label">{t(label)}: </span>
      <span
        className={`personalizzazione-ordine-detail__conf-value${
          !uppercaseValue ? ' personalizzazione-ordine-detail__conf-value--no-uppercase' : ''
        }`}
      >
        {value}
      </span>
    </p>
  )

  const formatTextStyle = (item) => {
    const family = item?.fontFamily || '-'
    const size = item?.fontSize ? parseFontSize(item.fontSize) : '-'
    const weight = item?.fontWeight || '-'
    const styleValues = []

    if (item?.fontStyle) styleValues.push(item.fontStyle)
    if (item?.underline) styleValues.push('underline')

    const style = styleValues.length ? styleValues.join(', ') : 'Normal'

    return `Font: Family: ${family} - Size: ${size} - Weight: ${weight} - Style: ${style}`
  }

  const renderEditorObjectDetails = (objectsData, legacyLoghi) => {
    if (!Array.isArray(objectsData) || objectsData.length === 0) return null

    const logoObjects = objectsData.filter((item) => item?.type === 'image')
    const textObjects = objectsData.filter((item) => item?.type === 'text')

    return (
      <>
        <div>
          <p className="personalizzazione-ordine-detail__editor-info__subtitle">{t('Loghi')}:</p>
          {logoObjects.length === 0 ? (
            <p>
              <Trans chiave="Nessun logo caricato" />
            </p>
          ) : (
            logoObjects.map((logo, index) => {
              const key = logo?.id || logo?.image || index
              const colori = formatEditorValue(logo?.colori)
              const larghezza = formatEditorValue(logo?.width)
              const altezza = formatEditorValue(logo?.height)
              const logoLegacy = legacyLoghi.find((l) => l?.immagine_svg?.url === logo?.image)

              return (
                <div key={key}>
                  <p>
                    {t('Logo')} {index + 1}
                    {larghezza ? ` - ${t('Larghezza')}: ${larghezza} cm` : ''}
                    {altezza ? ` - ${t('Altezza')}: ${altezza} cm` : ''}
                  </p>
                  <p>
                    {t('Colori')}: {colori || '-'}
                  </p>
                  <p>
                    {t('File')}:{' '}
                    <a
                      href={logo.original_file || logoLegacy?.file_originale?.url}
                      target="_blank"
                      rel="noreferrer"
                      className="personalizzazione-ordine-detail__editor-info__url"
                    >
                      {getFileBaseName(logo.original_file || logoLegacy?.file_originale?.url)}
                    </a>
                  </p>
                </div>
              )
            })
          )}
        </div>

        <div>
          <p className="personalizzazione-ordine-detail__editor-info__subtitle">{t('Testi')}:</p>
          {textObjects.length === 0 ? (
            <p>
              <Trans chiave="Nessun testo inserito" />
            </p>
          ) : (
            textObjects.map((textObj, index) => {
              const key = textObj?.id || `${textObj?.text || 'text'}-${index}`
              const contenuto = formatEditorValue(textObj?.text)
              const colore = formatEditorValue(textObj?.colore)
              const larghezza = formatEditorValue(textObj?.width)
              const altezza = formatEditorValue(textObj?.height)
              const stile = formatTextStyle(textObj)

              return (
                <div key={key}>
                  <p>
                    {t('Testo')} {index + 1}
                    {contenuto ? ` - "${contenuto}"` : ''}
                    {larghezza ? ` - ${t('Larghezza')}: ${larghezza} cm` : ''}
                    {altezza ? ` - ${t('Altezza')}: ${altezza} cm` : ''}
                  </p>
                  <p>
                    {t('Colore')}: {colore || '-'}
                  </p>
                  <p>{stile || '-'}</p>
                </div>
              )
            })
          )}
        </div>
      </>
    )
  }

  const renderEditorInfo = (personalizzazionePosizione) => {
    const editorState = personalizzazionePosizione?.editor_state

    if (!editorState) return null

    const objectsData = Array.isArray(editorState.objects_data) ? editorState.objects_data : []

    return (
      <div className="row personalizzazione-ordine-detail__editor-info nobreak">
        <div className="col personalizzazione-ordine-detail__editor-info__content">
          <p className="personalizzazione-ordine-detail__subtitle">{t('Con editor')}:</p>
          {renderEditorObjectDetails(objectsData, editorState.loghi)}
          {editorState.bozza && (
            <div>
              <p className="personalizzazione-ordine-detail__editor-info__subtitle">
                {t('Bozza del cliente')}:
              </p>
              <p>
                {t('Bozza digitale')}:{' '}
                <a
                  href={getEditorFileHref(editorState.bozza)}
                  target="_blank"
                  rel="noreferrer"
                  className="personalizzazione-ordine-detail__editor-info__url"
                >
                  {getEditorFileName(editorState.bozza)}
                </a>
              </p>
            </div>
          )}
        </div>
        {isFornitore && !!editorState?.bozza_thumb_url && (
          <div className="col">
            <div className="personalizzazione-ordine-detail__editor-info__image">
              <img src={editorState.bozza_thumb_url} />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderLoghi = () => {
    return (
      <>
        {mostraLoghi && (
          <StepItemOrdine placeholder="Caricamento loghi">
            <div className="row" style={{ rowGap: '12px' }}>
              {personalizzazione.personalizzazione_posizione_1 && (
                <div className="col">
                  <p className="personalizzazione-ordine-detail__subtitle">
                    <Trans chiave="Configurazione" />
                    {' 1:'}
                  </p>
                  {personalizzazione.personalizzazione_posizione_1.loghi?.length ? (
                    personalizzazione.personalizzazione_posizione_1.loghi.map(l => (
                      <div key={l.pk}>
                        <a href={l.url} target="_blank">
                          <p>{l.name ? l.name : 'Logo'}</p>
                        </a>
                      </div>
                    ))
                  ) : (
                    <p>
                      <Trans chiave="Nessun logo caricato" />
                    </p>
                  )}
                </div>
              )}
              {personalizzazione.personalizzazione_posizione_2 && (
                <div className="col">
                  <p className="personalizzazione-ordine-detail__subtitle">
                    <Trans chiave="Configurazione" />
                    {' 2:'}
                  </p>
                  {personalizzazione.stesso_logo ? (
                    <p>
                      <Trans chiave="Stesso logo" />
                    </p>
                  ) : personalizzazione.personalizzazione_posizione_2.loghi?.length ? (
                    personalizzazione.personalizzazione_posizione_2.loghi?.map(l => (
                      <div key={l.pk}>
                        <a href={l.url} target="_blank">
                          <p>{l.name ? l.name : 'Logo'}</p>
                        </a>
                      </div>
                    ))
                  ) : (
                    <p>
                      <Trans chiave="Nessun logo caricato" />
                    </p>
                  )}
                </div>
              )}
            </div>
          </StepItemOrdine>
        )}
      </>
    )
  }

  if (fromAdmin) {
    return (
      <div>
        <h3>
          <Trans chiave="Personalizzazione" /> {personalizzazione.nome_lavorazione}
        </h3>
        <div className="pdf__subsection">{renderLoghi()}</div>
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {personalizzazione.personalizzazione_posizione_1 ||
      personalizzazione.personalizzazione_posizione_2 ? (
        <h3>
          <Trans chiave="Personalizzazione" /> {personalizzazione.nome_lavorazione}
        </h3>
      ) : (
        <h3>
          <Trans chiave="Articoli non personalizzati" />
        </h3>
      )}
      <div className="form__personalizzazione__header">
        <div className="form__personalizzazione__header__content">
          {righe_per_personalizzazione.map((riga, i) => {
            return (
              <ProdottoPersonalizzazioneCard
                prodotto={riga.articolo}
                colore={riga.articolo.colore}
                quantita={riga.quantita}
                taglia={riga.articolo.taglia?.nome}
                key={i}
                conLink={true}
                fromPdf={true}
              />
            )
          })}
        </div>
      </div>
      {(personalizzazione.personalizzazione_posizione_1 ||
        personalizzazione.personalizzazione_posizione_2) && (
        <>
          <div className="pdf__subsection">
            <StepItemOrdine placeholder="Numero posizioni di stampa">
              {!personalizzazione.personalizzazione_posizione_2 ? (
                <p>
                  <Trans chiave="1 posizione" />
                </p>
              ) : personalizzazione.personalizzazione_posizione_2 &&
                personalizzazione.stesso_logo ? (
                <p>
                  <Trans chiave="2 posizioni con stesso logo" />
                </p>
              ) : (
                <p>
                  <Trans chiave="2 posizioni con loghi diversi" />
                </p>
              )}
            </StepItemOrdine>
            <StepItemOrdine placeholder="Configurazione di stampa">
              <div className="personalizzazione-ordine-detail__configurazioni">
                {personalizzazione.personalizzazione_posizione_1 && (
                  <div>
                    <div className="row">
                      <div className="col">
                        <p className="personalizzazione-ordine-detail__subtitle">
                          <Trans chiave="Configurazione" />
                          {' 1:'}
                        </p>

                        {renderField(
                          'Tecnica',
                          <>
                            {personalizzazione.personalizzazione_posizione_1.tecnica.nome}
                            {isFornitore &&
                            personalizzazione.personalizzazione_posizione_1.listino_codice
                              ? ` (${personalizzazione.personalizzazione_posizione_1.listino_codice})`
                              : ''}
                          </>
                        )}

                        {renderField(
                          'Posizione',
                          personalizzazione.personalizzazione_posizione_1.posizione.nome
                        )}

                        {personalizzazione.personalizzazione_posizione_1.descrizione_posizione &&
                          renderField(
                            'Note',
                            personalizzazione.personalizzazione_posizione_1.descrizione_posizione,
                            false
                          )}
                        {modificaDimensione ? (
                          <ModificaDimensione
                            personalizzazione={personalizzazione.personalizzazione_posizione_1}
                            personalizzazioni_righe_equivalenti={righe_per_personalizzazione.map(
                              (riga) => {
                                return riga.personalizzazione_posizione_1
                              }
                            )}
                            ordine={ordine}
                          />
                        ) : (
                          renderDimensione(personalizzazione.personalizzazione_posizione_1)
                        )}
                        {renderField(
                          'Colori',
                          personalizzazione.personalizzazione_posizione_1.numero_colori_posizione
                            .nome
                        )}
                        {modificaColori ? (
                          <ModificaColori
                            personalizzazione={personalizzazione.personalizzazione_posizione_1}
                            personalizzazioni_righe_equivalenti={righe_per_personalizzazione.map(
                              (riga) => {
                                return riga.personalizzazione_posizione_1
                              }
                            )}
                            ordine={ordine}
                          />
                        ) : (
                          <p>
                            {personalizzazione.personalizzazione_posizione_1.pantoni?.map(
                              (pantone, i) => {
                                return (
                                  <span key={`colore-${i}`}>
                                    <span className="personalizzazione-ordine-detail__conf-value personalizzazione-ordine-detail__conf-value--no-uppercase">{`${t(
                                      'Colore'
                                    )} ${i + 1} - `}</span>
                                    <span className="personalizzazione-ordine-detail__conf-value">
                                      {pantone.pantone}
                                    </span>
                                    {i !==
                                    personalizzazione.personalizzazione_posizione_1.pantoni.length -
                                      1
                                      ? ', '
                                      : ''}
                                  </span>
                                )
                              }
                            )}
                          </p>
                        )}
                      </div>
                      {!!getAreaStampa(
                        personalizzazione.personalizzazione_posizione_1,
                        personalizzazione.articolo
                      )?.immagine_thumb_url && (
                        <div className="col">
                          <div className="personalizzazione-ordine-detail__area-stampa-image">
                            <img
                              src={
                                getAreaStampa(
                                  personalizzazione.personalizzazione_posizione_1,
                                  personalizzazione.articolo
                                ).immagine_thumb_url
                              }
                              alt={t('Immagine articolo con area di stampa')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {renderEditorInfo(personalizzazione.personalizzazione_posizione_1)}
                  </div>
                )}

                {personalizzazione.personalizzazione_posizione_2 && (
                  <div>
                    <div className="row">
                      <div className="col">
                        <p className="personalizzazione-ordine-detail__subtitle">
                          <Trans chiave="Configurazione" />
                          {' 2:'}
                        </p>
                        {renderField(
                          'Tecnica',
                          <>
                            {personalizzazione.personalizzazione_posizione_2.tecnica.nome}
                            {isFornitore &&
                            personalizzazione.personalizzazione_posizione_2.listino_codice
                              ? ` (${personalizzazione.personalizzazione_posizione_2.listino_codice})`
                              : ''}
                          </>
                        )}
                        {renderField(
                          'Posizione',
                          personalizzazione.personalizzazione_posizione_2.posizione.nome
                        )}
                        {personalizzazione.personalizzazione_posizione_2.descrizione_posizione &&
                          renderField(
                            'Note',
                            personalizzazione.personalizzazione_posizione_2.descrizione_posizione,
                            false
                          )}
                        {modificaDimensione ? (
                          <ModificaDimensione
                            personalizzazione={personalizzazione.personalizzazione_posizione_2}
                            personalizzazioni_righe_equivalenti={righe_per_personalizzazione.map(
                              (riga) => {
                                return riga.personalizzazione_posizione_2
                              }
                            )}
                            ordine={ordine}
                          />
                        ) : (
                          renderDimensione(personalizzazione.personalizzazione_posizione_2)
                        )}
                        {renderField(
                          'Colori',
                          personalizzazione.personalizzazione_posizione_2.numero_colori_posizione
                            .nome
                        )}
                        {modificaColori ? (
                          <ModificaColori
                            personalizzazione={personalizzazione.personalizzazione_posizione_2}
                            personalizzazioni_righe_equivalenti={righe_per_personalizzazione.map(
                              (riga) => {
                                return riga.personalizzazione_posizione_2
                              }
                            )}
                            ordine={ordine}
                          />
                        ) : (
                          <p>
                            {personalizzazione.personalizzazione_posizione_2.pantoni?.map(
                              (pantone, i) => {
                                return (
                                  <span key={`colore-${i}`}>
                                    <span className="personalizzazione-ordine-detail__conf-value personalizzazione-ordine-detail__conf-value--no-uppercase">{`${t(
                                      'Colore'
                                    )} ${i + 1} - `}</span>
                                    <span className="personalizzazione-ordine-detail__conf-value">
                                      {pantone.pantone}
                                    </span>
                                    {i !==
                                    personalizzazione.personalizzazione_posizione_2.pantoni.length -
                                      1
                                      ? ', '
                                      : ''}
                                  </span>
                                )
                              }
                            )}
                          </p>
                        )}
                      </div>
                      {!!getAreaStampa(
                        personalizzazione.personalizzazione_posizione_2,
                        personalizzazione.articolo
                      )?.immagine_thumb_url && (
                        <div className="col">
                          <div className="personalizzazione-ordine-detail__area-stampa-image">
                            <img
                              src={
                                getAreaStampa(
                                  personalizzazione.personalizzazione_posizione_2,
                                  personalizzazione.articolo
                                ).immagine_thumb_url
                              }
                              alt={t('Immagine articolo con area di stampa')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {renderEditorInfo(personalizzazione.personalizzazione_posizione_2)}
                  </div>
                )}
              </div>
            </StepItemOrdine>
            {renderLoghi()}
            <StepItemOrdine placeholder="Tipo confezionamento">
              {personalizzazione.confezionamento_richiesto_personalizzazione !== 'D' ? (
                <>
                  <p>{personalizzazione.confezionamento?.nome}</p>
                  <p>
                    <Trans chiave="Richiesto:" />{' '}
                    {personalizzazione.confezionamento_richiesto ? (
                      <Trans chiave="SI" />
                    ) : (
                      <Trans chiave="NO" />
                    )}
                  </p>
                </>
              ) : (
                <p>{personalizzazione.confezionamento?.nome}</p>
              )}
            </StepItemOrdine>
          </div>

          <div className="pdf__subsection nobreak">
            {mostraNote && righe_per_personalizzazione.some((riga) => riga.nota) && (
              <>
                <p>
                  <strong>
                    <Trans chiave="Note di lavorazione:" />
                  </strong>{' '}
                </p>
                {righe_per_personalizzazione.map(
                  (riga) =>
                    riga.nota && (
                      <p>
                        {riga.articolo.codice} - <Trans chiave="note: " /> {riga.nota}
                      </p>
                    )
                )}
              </>
            )}
          </div>
          {mostraPrezziFornitore && personalizzazioneFornitore && (
            <div className="pdf__data-table nobreak" style={{ paddingTop: '24px' }}>
              <div className="pdf__data-row pdf__data-row--big">
                <span>
                  <Trans chiave="Costo personalizzazione:" />
                </span>
                <span>
                  {priceNotation(personalizzazioneFornitore.valorePersonalizzazione, 2)} €
                </span>
              </div>
              {personalizzazioneFornitore.valoreConfezionamento !== 0 && (
                <div className="pdf__data-row pdf__data-row--big">
                  <span>
                    <Trans chiave="Costo confezionamento:" />
                  </span>
                  <span>
                    {priceNotation(personalizzazioneFornitore.valoreConfezionamento, 2)} €
                  </span>
                </div>
              )}
            </div>
          )}
          {mostraBozza && (
            <div className="pdf__bozza nobreak">
              <img src={righe_per_personalizzazione[0].bozza_digitale_thumb} />
            </div>
          )}
          {uploadBozzaDigitale && (
            <div>
              <UploadBozzaDigitale
                personalizzazione={personalizzazione}
                ordine={ordine}
                handleUploadBozzaDigitale={handleUploadBozzaDigitale}
                righe={righe_per_personalizzazione}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PersonalizzazioneOrdineDetail
